import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Edit, Trash2, Plus, Save } from 'lucide-react';
import { useLernsets } from './LearnsetContext';
import { Card } from '../util/types';

const CardList = () => {
    const { id } = useParams<{ id: string }>();
    const { getLernsetById, updateLernset } = useLernsets();
    const lernset = getLernsetById(id!);

    const [editingCardId, setEditingCardId] = useState<string | null>(null);
    const [editQuestion, setEditQuestion] = useState('');
    const [editAnswer, setEditAnswer] = useState('');

    const [showAddForm, setShowAddForm] = useState(false);
    const [newQuestion, setNewQuestion] = useState('');
    const [newAnswer, setNewAnswer] = useState('');

    if (!lernset) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Lernset nicht gefunden</h1>
                <Link to="/lernsets" className="text-indigo-600 hover:underline">
                    Zurück zu meinen Lernsets
                </Link>
            </div>
        );
    }

    const handleEditStart = (card: Card) => {
        setEditingCardId(card.id);
        setEditQuestion(card.question);
        setEditAnswer(card.answer);
    };

    const handleEditSave = async () => {
        if (!editingCardId) return;
        const updatedCards = lernset.cards.map((c: Card) =>
            c.id === editingCardId
                ? { ...c, question: editQuestion, answer: editAnswer }
                : c
        );
        await updateLernset(id!, { cards: updatedCards });
        setEditingCardId(null);
    };

    const handleDelete = async (cardId: string) => {
        if (window.confirm('Diese Karte wirklich löschen?')) {
            const updatedCards = lernset.cards.filter((c: Card) => c.id !== cardId);
            await updateLernset(id!, { cards: updatedCards });
        }
    };

    const handleAddCard = async () => {
        if (!newQuestion.trim() || !newAnswer.trim()) return;
        const now = Date.now();
        const newCard: Card = {
            id: `${now}_${Math.random().toString(36).substr(2, 9)}`,
            question: newQuestion.trim(),
            answer: newAnswer.trim(),
            strength: 50,
            nextDueAt: now,
            lastReviewedAt: now,
            reviewCount: 0,
            createdAt: now,
        };
        const updatedCards = [...lernset.cards, newCard];
        await updateLernset(id!, { cards: updatedCards });
        setNewQuestion('');
        setNewAnswer('');
        setShowAddForm(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-4xl mx-auto">
                <Link
                    to={`/lernset/${id}`}
                    className="inline-flex items-center text-gray-600 hover:text-indigo-600 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Zurück zum Dashboard
                </Link>

                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                    Karteikarten von "{lernset.name}"
                </h1>
                <p className="text-gray-600 mb-6">{lernset.cards.length} Karten insgesamt</p>

                <button
                    onClick={() => setShowAddForm(true)}
                    className="mb-6 px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    Neue Karte hinzufügen
                </button>

                <AnimatePresence>
                    {showAddForm && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden mb-6"
                        >
                            <div className="bg-white/90 rounded-xl p-4 border border-indigo-200 shadow-md">
                                <h3 className="font-semibold mb-3">Neue Karte</h3>
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        placeholder="Frage"
                                        value={newQuestion}
                                        onChange={(e) => setNewQuestion(e.target.value)}
                                        className="w-full p-2 border rounded"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Antwort"
                                        value={newAnswer}
                                        onChange={(e) => setNewAnswer(e.target.value)}
                                        className="w-full p-2 border rounded"
                                    />
                                    <div className="flex gap-2 justify-end">
                                        <button
                                            onClick={() => setShowAddForm(false)}
                                            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                                        >
                                            Abbrechen
                                        </button>
                                        <button
                                            onClick={handleAddCard}
                                            className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                        >
                                            Speichern
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="space-y-3">
                    {lernset.cards.map((card: Card) => (
                        <div
                            key={card.id}
                            className="bg-white/80 rounded-xl p-4 border border-gray-200 shadow-sm flex items-start gap-4"
                        >
                            {editingCardId === card.id ? (
                                <div className="flex-1 space-y-2">
                                    <input
                                        type="text"
                                        value={editQuestion}
                                        onChange={(e) => setEditQuestion(e.target.value)}
                                        className="w-full p-2 border rounded"
                                    />
                                    <input
                                        type="text"
                                        value={editAnswer}
                                        onChange={(e) => setEditAnswer(e.target.value)}
                                        className="w-full p-2 border rounded"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleEditSave}
                                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                        >
                                            <Save className="w-4 h-4 inline mr-1" />
                                            Speichern
                                        </button>
                                        <button
                                            onClick={() => setEditingCardId(null)}
                                            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                                        >
                                            Abbrechen
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-800">{card.question}</p>
                                        <p className="text-gray-600 mt-1">{card.answer}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEditStart(card)}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(card.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CardList;